import React, { useState, useCallback } from 'react';
import { 
  Upload, Image, Wand2, Download, Check, X, Loader2, 
  ZoomIn, Contrast, FileImage, Printer, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
});

export default function ArtworkEnhancer() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [settings, setSettings] = useState({
    artworkType: 'auto', // auto, pen-ink, color
    enableBW: true,
    enableUpscale: true,
    generatePrints: true,
    targetSize: 'large' // small, medium, large
  });

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setFile(selectedFile);
    setResults(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(selectedFile);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      setFile(droppedFile);
      setResults(null);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(droppedFile);
    }
  }, []);

  const processArtwork = async () => {
    if (!file) return;

    setProcessing(true);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('artwork_type', settings.artworkType);
      formData.append('enable_bw', settings.enableBW);
      formData.append('enable_upscale', settings.enableUpscale);
      formData.append('generate_prints', settings.generatePrints);
      formData.append('target_size', settings.targetSize);

      const response = await fetch(`${API}/api/artwork-enhance/process`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Processing failed');
      }

      const data = await response.json();
      setResults(data);
      toast.success('Artwork enhanced successfully!');
    } catch (error) {
      console.error('Processing error:', error);
      toast.error(error.message || 'Failed to process artwork');
    } finally {
      setProcessing(false);
    }
  };

  const addToStore = async () => {
    if (!results) return;

    try {
      const title = prompt('Enter artwork title:', file?.name?.replace(/\.[^/.]+$/, '') || 'Untitled');
      if (!title) return;

      const price = prompt('Enter price ($):', '500');
      if (!price) return;

      const response = await fetch(`${API}/api/artwork-enhance/add-to-store`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title,
          price: parseFloat(price),
          processing_id: results.processing_id,
          artwork_type: results.detected_type
        })
      });

      if (!response.ok) throw new Error('Failed to add to store');

      toast.success('Artwork added to store!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Wand2 className="w-8 h-8 text-primary" />
            Artwork Enhancement Studio
          </h1>
          <p className="text-muted-foreground mt-2">
            Upload artwork to enhance, upscale, and prepare for print sales
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Upload & Settings */}
          <div className="space-y-6">
            {/* Upload Area */}
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${
                preview ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
            >
              {preview ? (
                <div className="space-y-4">
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-h-64 mx-auto rounded-lg shadow-lg"
                  />
                  <p className="text-sm text-muted-foreground">{file?.name}</p>
                  <button
                    onClick={() => { setFile(null); setPreview(null); setResults(null); }}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-foreground font-medium">Drop artwork here or click to upload</p>
                  <p className="text-muted-foreground text-sm mt-2">
                    Supports JPG, PNG, WebP
                  </p>
                </label>
              )}
            </div>

            {/* Settings */}
            <div className="bg-card rounded-2xl p-6 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Image className="w-5 h-5" />
                Enhancement Settings
              </h3>

              <div className="space-y-4">
                {/* Artwork Type */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Artwork Type
                  </label>
                  <select
                    value={settings.artworkType}
                    onChange={(e) => setSettings({ ...settings, artworkType: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                  >
                    <option value="auto">Auto-Detect</option>
                    <option value="pen-ink">Pen & Ink (Black & White)</option>
                    <option value="color">Color Painting/Photo</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pen & ink will be converted to crisp B&W. Color will preserve original colors.
                  </p>
                </div>

                {/* Toggles */}
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enableBW}
                      onChange={(e) => setSettings({ ...settings, enableBW: e.target.checked })}
                      className="w-4 h-4 accent-primary"
                    />
                    <div>
                      <span className="text-foreground">Convert to Crisp B&W</span>
                      <p className="text-xs text-muted-foreground">For pen & ink artwork only</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enableUpscale}
                      onChange={(e) => setSettings({ ...settings, enableUpscale: e.target.checked })}
                      className="w-4 h-4 accent-primary"
                    />
                    <div>
                      <span className="text-foreground">Upscale to Print Resolution</span>
                      <p className="text-xs text-muted-foreground">300 DPI for selected print size</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.generatePrints}
                      onChange={(e) => setSettings({ ...settings, generatePrints: e.target.checked })}
                      className="w-4 h-4 accent-primary"
                    />
                    <div>
                      <span className="text-foreground">Generate Print Files</span>
                      <p className="text-xs text-muted-foreground">Create all 3 print sizes</p>
                    </div>
                  </label>
                </div>

                {/* Target Size */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Max Print Size
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'small', label: '8×10"', px: '2400×3000' },
                      { value: 'medium', label: '11×14"', px: '3300×4200' },
                      { value: 'large', label: '16×20"', px: '4800×6000' }
                    ].map((size) => (
                      <button
                        key={size.value}
                        onClick={() => setSettings({ ...settings, targetSize: size.value })}
                        className={`p-3 rounded-lg border text-center transition-colors ${
                          settings.targetSize === size.value
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50 text-foreground'
                        }`}
                      >
                        <div className="font-medium">{size.label}</div>
                        <div className="text-xs text-muted-foreground">{size.px}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Process Button */}
              <button
                onClick={processArtwork}
                disabled={!file || processing}
                className="w-full mt-6 py-3 bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-black rounded-xl font-bold flex items-center justify-center gap-2 transition-colors"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    Enhance Artwork
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Right Panel - Results */}
          <div className="space-y-6">
            {results ? (
              <>
                {/* Analysis Results */}
                <div className="bg-card rounded-2xl p-6 border border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    Enhancement Complete
                  </h3>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-background rounded-lg p-3">
                      <div className="text-muted-foreground">Original Size</div>
                      <div className="text-foreground font-medium">
                        {results.original_size?.[0]} × {results.original_size?.[1]}
                      </div>
                    </div>
                    <div className="bg-background rounded-lg p-3">
                      <div className="text-muted-foreground">Enhanced Size</div>
                      <div className="text-foreground font-medium">
                        {results.enhanced_size?.[0]} × {results.enhanced_size?.[1]}
                      </div>
                    </div>
                    <div className="bg-background rounded-lg p-3">
                      <div className="text-muted-foreground">Detected Type</div>
                      <div className="text-foreground font-medium capitalize">
                        {results.detected_type || 'Unknown'}
                      </div>
                    </div>
                    <div className="bg-background rounded-lg p-3">
                      <div className="text-muted-foreground">DPI @ 16×20"</div>
                      <div className={`font-medium ${results.dpi_16x20 >= 300 ? 'text-green-400' : 'text-amber-400'}`}>
                        {results.dpi_16x20} DPI
                      </div>
                    </div>
                  </div>

                  {/* Quality Assessment */}
                  <div className="mt-4 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center gap-2 text-green-400 font-medium">
                      <Check className="w-5 h-5" />
                      {results.dpi_16x20 >= 300 
                        ? 'Print Ready! Meets 300 DPI requirement'
                        : `Upscaled to ${results.dpi_16x20} DPI - Good for smaller prints`
                      }
                    </div>
                  </div>
                </div>

                {/* Generated Files */}
                {results.files && (
                  <div className="bg-card rounded-2xl p-6 border border-border">
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <FileImage className="w-5 h-5" />
                      Generated Files
                    </h3>

                    <div className="space-y-3">
                      {results.files.enhanced && (
                        <a
                          href={`${API}${results.files.enhanced}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-3 bg-background rounded-lg hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <ZoomIn className="w-5 h-5 text-blue-400" />
                            <span className="text-foreground">Enhanced Image</span>
                          </div>
                          <Download className="w-4 h-4 text-muted-foreground" />
                        </a>
                      )}

                      {results.files.bw && (
                        <a
                          href={`${API}${results.files.bw}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-3 bg-background rounded-lg hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Contrast className="w-5 h-5 text-white" />
                            <span className="text-foreground">Crisp B&W Version</span>
                          </div>
                          <Download className="w-4 h-4 text-muted-foreground" />
                        </a>
                      )}

                      {results.files.svg && (
                        <a
                          href={`${API}${results.files.svg}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-3 bg-background rounded-lg hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <FileImage className="w-5 h-5 text-purple-400" />
                            <span className="text-foreground">SVG Vector</span>
                          </div>
                          <Download className="w-4 h-4 text-muted-foreground" />
                        </a>
                      )}

                      {results.print_files && Object.entries(results.print_files).map(([size, info]) => (
                        <a
                          key={size}
                          href={`${API}/api/artwork-prints/print-file/${info.filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-3 bg-background rounded-lg hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Printer className="w-5 h-5 text-green-400" />
                            <span className="text-foreground">Print - {info.size_name}</span>
                          </div>
                          <Download className="w-4 h-4 text-muted-foreground" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add to Store Button */}
                <button
                  onClick={addToStore}
                  className="w-full py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors"
                >
                  <Check className="w-5 h-5" />
                  Add to Art Gallery Store
                </button>
              </>
            ) : (
              <div className="bg-card rounded-2xl p-12 border border-border text-center">
                <Image className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Results Yet</h3>
                <p className="text-muted-foreground">
                  Upload an artwork and click "Enhance Artwork" to see results
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-card rounded-2xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Upload, title: '1. Upload', desc: 'Drop your artwork image' },
              { icon: Wand2, title: '2. Enhance', desc: 'Auto-detect type & enhance quality' },
              { icon: Printer, title: '3. Generate', desc: 'Create print-ready files (3 sizes)' },
              { icon: Check, title: '4. Publish', desc: 'Add to store with one click' }
            ].map((step, i) => (
              <div key={i} className="text-center">
                <step.icon className="w-8 h-8 text-primary mx-auto mb-2" />
                <h4 className="font-medium text-foreground">{step.title}</h4>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
